// React
import { ReactElement } from 'react';
// Utils
import { getColorStyle } from '@utmb/utils/colors';
import { useTranslation } from 'next-i18next';
// Interfaces
import { ColorTypesEnum } from '@utmb/types/colors';
import { RaceItemView } from '@utmb/types/index';
// Design System
import { Font } from '@utmb/design-system/text/font';
import { Image } from '@utmb/design-system/image/Image';
// Features
import DotIcon from '@utmb/features/races/races-list/DotIcon';
// Styles
import styles from './races-list.module.scss';
import { Placeholder } from '@utmb/design-system/placeholder/Placeholder';
import RunningStones from '@utmb/design-system/running-stone/RunningStones';
import { RaceRegistrationStatusEnum } from '@utmb/types/enum/RaceRegistrationStatusEnum';
import { usePreference } from '@utmb/contexts/PreferenceContext';
import { formatUnit } from '@utmb/utils/unit';

interface ImageCardProps {
    raceItem: RaceItemView;
    isOpen: boolean;
}

const ImageCard = (props: ImageCardProps): ReactElement => {
    const { raceTheme, raceThemeIsDark, media, startDate, raceStatus, details } = props.raceItem;
    const { isOpen } = props;

    const {
        preferences: { preferredUnit },
    } = usePreference();

    const runningStone = details.statsUp.find((stat) => stat.name === 'runningStones');
    const distance = details.statsUp.find((stat) => stat.name === 'distance');

    const { t } = useTranslation();

    return (
        <div className={styles.card_image}>
            <div className={styles.image_wrapper}>{media ? <Image objectFit="cover" layout="fill" image={media} /> : <Placeholder />}</div>

            {raceStatus && (
                <div className={styles.status}>
                    <Font.OxaniumBold mobile="14" desktop="16">
                        {raceStatus.open ? (
                            <>
                                <DotIcon color="green" /> {t(`racesList.registration_open`)}
                            </>
                        ) : raceStatus.status === RaceRegistrationStatusEnum.AVAILABLE_SOON ? (
                            <>
                                <DotIcon color="#fd4b01" /> {t(`racesList.available_soon`)}
                            </>
                        ) : (
                            <>
                                <DotIcon color="red" /> {t(`racesList.${raceStatus.status}`)}
                            </>
                        )}
                    </Font.OxaniumBold>
                </div>
            )}

            {startDate && (
                <Font.OxaniumRegular
                    mobile="16"
                    className={styles.date_open}
                    style={{
                        backgroundColor: `${raceTheme}`,
                        color: raceThemeIsDark ? getColorStyle(ColorTypesEnum.WHITE) : getColorStyle(ColorTypesEnum.DEFAULT),
                    }}
                >
                    {startDate}
                </Font.OxaniumRegular>
            )}

            {runningStone && (
                <div className={[styles.runningStone, isOpen ? styles.cardOpen : ''].join(' ')}>
                    <RunningStones quantity={+runningStone.value} />
                </div>
            )}

            {distance && (
                <Font.OxaniumBold
                    mobile="18"
                    desktop="20"
                    color={getColorStyle(ColorTypesEnum.WHITE)}
                    className={[styles.distance, isOpen ? styles.cardOpen : ''].join(' ')}
                >
                    {formatUnit(preferredUnit, 'distance', Number(distance.value))}
                </Font.OxaniumBold>
            )}
        </div>
    );
};

export default ImageCard;
